import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BiLogInCircle, BiUserPlus, BiLock, BiLoader } from 'react-icons/bi';
import Alert from '../components/Alert';
import { authService } from '../services/authService';
import { buildAuthUrl, getRedirectUrl } from '../utils/url';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage('');
    setMessageType('info');

    try {
      const response = await authService.login(email, password);

      console.log(response);

      if (response.success) {
        const redirectUrl = getRedirectUrl(searchParams);
        const destination = redirectUrl ? decodeURIComponent(redirectUrl) : '/agencies';

        setMessage(`Login successful! Redirecting...`);
        setMessageType('success');
        setTimeout(() => {
          navigate(destination);
        }, 1500);
      } else {
        setMessage(response.message || 'Login failed. Please check your credentials.');
        setMessageType('error');
      }
    } catch (error: any) {
      console.error('Login error in component:', error);
      setMessage(error.message || 'An unexpected error occurred');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 space-y-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white">Welcome Back!</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">Sign in to continue to your account.</p>
        </div>

        {message && <Alert type={messageType} message={message} onClose={() => setMessage('')} />}

        <form className="space-y-6" onSubmit={handleLogin}>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="you@example.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 border-slate-200 dark:border-slate-600 text-slate-900 dark:text-white"
              placeholder="••••••••"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input id="remember-me" name="remember-me" type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-slate-300 rounded" />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-slate-900 dark:text-slate-300">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link
                to={buildAuthUrl('/auth/forgot-password', getRedirectUrl(searchParams))}
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <BiLoader className="animate-spin mr-2" />
                  Signing In...
                </>
              ) : (
                <>
                  <BiLogInCircle className="mr-2" />
                  Sign In
                </>
              )}
            </button>
          </div>
        </form>

        <div className="text-center text-sm text-slate-500 dark:text-slate-400">
          <p>
            Don't have an account?{' '}
            <Link
              to={buildAuthUrl('/auth/register', getRedirectUrl(searchParams))}
              className="font-medium text-primary-600 hover:text-primary-500 flex items-center justify-center"
            >
              <BiUserPlus className="mr-1" />
              Create one now
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
